import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import _ from "lodash";
import cx from "classnames";

import TextareaAutosize from "react-autosize-textarea";
import { BannerView } from "src/components/banner";
import { Dropdown } from "react-bootstrap";
import { ScrollableContainer } from "src/components/scroller";

import Slide from "../../../components/slide";
import Text from "src/components/text";
import { Simulation } from "src/types/coaching/simulation";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Banner } from "src/types/common";
import { updateSimulationProperty } from "src/redux/actions/coaching/simulation";
import { EditCourseThumbnail } from "src/pages/coaching/manage/courses/edit/thumbnail";
import { SelectSkillsDialog } from "src/pages/coaching/manage/courses/edit/components/selectSkills";
import { CourseLearners } from "src/pages/coaching/manage/courses/edit/components/learners";
import { CALL_TYPES, DIFFICULTY_CHOICES, DURATION_CHOICES } from "src/constants/simulation";
import { FileService } from "src/utils/file.service";
import { ErrorMessage } from "../../../components/error";
import { SimulationContext } from "../../context";
import { AssessmentConfiguration, AssessmentParameter } from "src/types/coaching/assessmentConfiguration";
import { useSpring, animated } from "@react-spring/web";

import styles from "./callSettings.module.scss";
import pluralize from "pluralize";
import Thumbnail from "src/components/thumbnail";

type EntityType = "discovery" | "coldCall";

const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'German', value: 'de' },
  { label: 'French', value: 'fr' },
  { label: 'Marathi', value: 'mr' },
];

interface CallSettingsProps {
  simulation: Simulation;
}

export const CallSettings: React.FC<CallSettingsProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = useContext(SimulationContext);

  const id = simulation.id;

  const learners = useSelector((state: any) => {
    return _.get(state, "courses.learners.items") || [];
  });

  const userProfile = useMemo(()=> _.get(simulation,"publishedBy.icon",null), [simulation])

  const persistedSimulation = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation.${simulation.id}.item`)
  })

  const published = useSelector((state: any) =>
    _.get(state, 'coaching.simulations.publish') 
  ); 

  const name = useMemo(() => {
    return _.get(simulation, "name") || "";
  }, [simulation]);

  const type = useMemo(() => _.get(simulation, "type",null), [simulation])

  const callType = useMemo(() => {
      const value = _.get(simulation, "type") || "";
      return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);


  const description = useMemo(() => {
    return _.get(simulation, "description") || "";
  }, [simulation]);

  const language = useMemo(() => {
    return _.get(simulation, "language") || "en";
  }, [simulation]);


  const banner = useMemo(() => {
    return _.get(simulation, "banner");
  }, [simulation]);

  const duration = useMemo(() => {
    return _.get(simulation, "duration");
  }, [simulation]);

  const difficulty = useMemo(() => {
    return _.get(simulation, "difficulty");
  }, [simulation]);

  const publishedBy = _.get(simulation, "publishedBy");

  const averageScore = useMemo(() => {
    return _.get(simulation, "averageScore", 0);
  }, [simulation]);

  const completedLearners = useMemo(() => {
    return _.get(simulation, "completedLearners", 0);
  }, [simulation]);

  const completedLearnerAttempts = useMemo(() => {
    return _.get(simulation, "completedLearnerAttempts", 0);
  }, [simulation]);

  const totalLearners = useMemo(() => {
    return _.get(simulation, "totalLearners", 0);
  }, [simulation]);

  const averageTimeSpent = useMemo(() => {
    return _.get(simulation, "averageTimeSpent", 0);
  }, [simulation]);

  const activeScorecard = simulationContext.activeAssessmentScorecard || simulation.assessmentConfiguration;
  
  const parameters: AssessmentParameter[] = useMemo(() => {
    return activeScorecard?.parameters || [];
  }, [activeScorecard]);

  const nameInputRef = React.useRef<HTMLTextAreaElement>(null);
  const descriptionInputRef = React.useRef<any>(null);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "name" });
  });

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "banner" });
  });

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "description" });
  });
  
  const isPublished = useMemo(()=> _.get(published,'published', false),[published]) as boolean
  const publishedSimulation = useMemo(() => _.get(published, 'item'), [published])


  const publishedByName = useMemo(() => {
    const source = _.get(publishedSimulation,"publishedBy") || _.get(persistedSimulation,"publishedBy");
    const firstName = _.get(source, "firstName", "");
    const lastName = _.get(source, "lastName", "");
    return `${firstName} ${lastName}`.trim();
  }, [publishedSimulation, persistedSimulation]);


  const [editThumbnail, setEditThumbnail] = useState(false);
  const [showSkillSlider, setShowSkillSlider] = useState(false);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(updateSimulationProperty({
      simulation: simulation.id!,
      property,
      value,
    }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleDescriptionChange = (e: any) => {
    console.log("Description changed", e.target.value);
    const content = e.target.value;
    handlePropertyChange("description", content);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const handleDurationChange = (duration: any) => {
    handlePropertyChange("duration", duration);
  };

  const handleDifficultyChange = (difficulty: any) => {
    handlePropertyChange("difficulty", difficulty);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  }

  const handleSkillRemove = (skill: any) => {
    const items = simulation.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  const handleEditAssessmentScoreCard = (value: boolean) => {
    simulationContext.editAssessmentScoreCard?.(value);
  }

  const getAvgScoreMetric = (averageScore: number, completedLearnerAttempts: number) => {  
    return {
      value: completedLearnerAttempts > 0 ? `${Math.round((averageScore) * 100)}%`  : "na",
      subTitle: completedLearnerAttempts > 0
        ? `across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`
        : "No learners have taken the learning yet"
    };
  };
  
  const getCompletionInfo = (completed: number, total: number) => {
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const text = `${completed} of ${total} learners have completed`;
  
    return { rate, text };
  };

  const subParameterCount = parameters.reduce(
    (sum, item) => sum + item.parameters.length,
    0
  );
  
  const avgScoreMetric = getAvgScoreMetric(averageScore, completedLearnerAttempts);
  const completionInfo = getCompletionInfo(completedLearners, totalLearners);

  useEffect(() => {
    return () => {
      console.log("Unmounting CallSettings");
    }
  }, []);

  useEffect(() => {
    if (simulationContext.validationError) {
      const type = simulationContext.validationError.type;
      const property = simulationContext.validationError.property;

      switch (type) {
        case "call-settings": {
          if (property === "name") {
            setTimeout(() => {
              nameInputRef.current?.focus();
            }, 1000);
          } else if (property === "description") {
            setTimeout(() => {
              descriptionInputRef.current?.focus();
            }, 1000);
          } else if (property === "skills") {
            setShowSkillSlider(true);
          }

          simulationContext.gotoError?.(undefined);

          break;
        }

        default: {
          break;
        }
      }
    }
  }, [simulationContext.validationError]);


  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
      <div className={styles.left}>
        <div className={styles.details}>
          <div className={styles.headerContainer}>
            <div className={styles.header}>
              <div
                className={cx(styles.field, styles.thumbnail)}
                onClick={() => {
                  setEditThumbnail(true);
                }}
              >
                <div className={styles.inner}>
                  <div className={styles.overlay}>
                    <i className="paperflite-edit"></i>
                  </div>
                  <div
                    className={cx(
                      styles.pic,
                      (type === "discovery" && styles.discovery) ||
                        (type === "cold_call" && styles.coldcall),
                    )}
                  >
                    <BannerView
                      banner={banner as any}
                      defaultBanner="/images/assets/simulationThumbnail.jpg"
                      overlay={false}
                    />
                  </div>
                </div>
                <ErrorMessage error={bannerError} />
              </div>
              <div className={cx(styles.field, styles.name)}>
                <TextareaAutosize
                  className={styles.input}
                  ref={nameInputRef}
                  placeholder="Scenario name goes here..."
                  rows={1}
                  maxRows={10}
                  value={name}
                  onChange={handleNameChange}
                  onPointerEnterCapture={() => {}}
                  onPointerLeaveCapture={() => {}}
                />
                <ErrorMessage error={nameError} />
              </div>
            </div>
            {(isPublished || publishedBy) &&
            <div className={styles.simulationMetadata}>
              <div className={styles.metadata}>
                <i className="paperflite-clock" />
                {`${simulation.duration} Minutes`}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-calendar" />
                {callType}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-speedomenter" />
                {difficulty}
              </div>
              <div className={cx(styles.metadata, styles.profile)}>
                <Thumbnail
                  media={userProfile}
                  defaultImage="/images/assets/default_asset.jpg"
                  className={styles.icon}
                />
                by {publishedByName}
              </div>
            </div>
            }
          </div>

          <div className={cx(styles.field, styles.description)}>
            <div className={styles.label}>Description</div>
            <TextareaAutosize
              className={styles.descriptionInput}
              rows={1}
              value={description}
              placeholder={
                "Describe the scenario to set the stage for your reps.\nInclude helpful context and key pointers to guide them through the simulation.\nMake it clear so they can confidently navigate the experience!..."
              }
              spellCheck={true}
              onChange={handleDescriptionChange}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <ErrorMessage error={descriptionError} />
          </div>

          <div className={cx(styles.field,styles.languageSettings)}>
          <div className={styles.label}>Preferred Language</div>
            <p className={styles.subtext}>
              Choose the language the prospect prefers for this call. The conversation will be guided in the selected language.
            </p>
            <PreferredLanguage value={language} onChange={handlePropertyChange} />
          </div>

          <div className={cx(styles.field, styles.duration)}>
            <div className={styles.label}>Call Duration</div>
            <p className={styles.subtext}>
              Set a time limit: short calls for specific skills, longer ones for
              full interactions
            </p>
            <Slide.Choice
              value={duration}
              options={DURATION_CHOICES}
              onChange={handleDurationChange}
            />
          </div>

          <div className={cx(styles.field, styles.difficulty)}>
            <div className={styles.label}>Challenge Level</div>
            <p className={styles.subtext}>
              Set the challenge level to match the skills you want reps to
              master
            </p>
            <Slide.Choice
              value={difficulty}
              options={DIFFICULTY_CHOICES}
              onChange={handleDifficultyChange}
            />
          </div>

          <div className={cx(styles.field, styles.scoreCard)}>
            <div className={styles.heading}>
              <div className={styles.title}>
                <div className={styles.label}>
                  <Text text={`Assessment Framework - ${activeScorecard?.name ?? ""}`} lines={1} /></div>
                <p className={styles.description}>
                  <Text text={activeScorecard?.description || ""} lines={2} />
                </p>
              </div>
              <div className={styles.actions}>
                <div className={styles.actionItem}>
                  <i className={cx(styles.icon, "paperflite-eye")} />
                  <span onClick={() => handleEditAssessmentScoreCard(true)}>review</span>
                </div>
              </div>
            </div>
            <div className={styles.metadata}>
              <div className={styles.statItem}>
                <div className={styles.value}>{subParameterCount.toString().padStart(2, "0")}</div>
                <div className={styles.label}>{pluralize("parameter", _.size(parameters))}</div>
              </div>
              <div className={styles.divider} />
              <div className={styles.statItem}>
                <div className={styles.value}>{`${parameters.reduce((sum, item) => sum + item.parameters.length, 0).toString().padStart(2, "0")}`}</div>
                <div className={styles.label}>{pluralize("sub-parameter", subParameterCount)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <div className={styles.right}>
        {(isPublished || publishedBy) && (
          <ScrollableContainer
            distance={300}
            leftArrowComponent={LeftArrow}
            rightArrowComponent={RightArrow}
          >
            <div className={styles.infoContainer}>
              <Metrics
                name="completion rate"
                value={completionInfo.rate + "%"}
                subTitle={completionInfo.text}
              />

              <Metrics
                name="avg score"
                value={avgScoreMetric.value}
                subTitle={avgScoreMetric.subTitle}
              />

              <Metrics
                name="avg time spent"
                value={FileService.formatDuration(averageTimeSpent, "short")}
              />
            </div>
          </ScrollableContainer>
        )}
        <p className={styles.heading}>Skills in focus ({0})</p>
        <div className={styles.skills}>
          <div
            className={styles.addSkillButton}
            onClick={() => onSkillClicked()}
          >
            <i className={cx(styles.icon, "paperflite-add")}></i>
          </div>

          {(simulation.skills || []).map((skill: any) => {
            return (
              <div key={skill.id} className={styles.skill}>
                <div className={styles.name}>{skill.name}</div>
                <i
                  className={cx("paperflite-close", styles.closeIcon)}
                  onClick={() => handleSkillRemove(skill)}
                ></i>
              </div>
            );
          })}
        </div>
        <div className={styles.courseLearners}>
          <CourseLearners id={simulation.id!} course={simulation} />
        </div>
      </div>

      {editThumbnail && (
        <EditCourseThumbnail
          show={editThumbnail}
          type="simulation"
          entityType={simulation?.type === "cold_call" ? "coldCall" : simulation?.type as EntityType}
          banner={simulation?.banner || null}
          defaultBanner="/images/assets/simulationThumbnail.jpg"
          onFinish={handleThumbnailChange}
          onClose={() => setEditThumbnail(false)}
        />
      )}

      {showSkillSlider && (
        <SelectSkillsDialog
          show={showSkillSlider}
          selected={simulation.skills || []}
          onFinish={handleAddSkillsFinish}
          onClose={() => setShowSkillSlider(false)}
        />
      )}
    </div>
  );
}


interface MetricsProps {
  name: string;
  value?: any;
  subTitle?: string;
}

const Metrics: React.FC<MetricsProps> = ({name, value, subTitle}) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={styles.name}>{name}</span>
        <span className={styles.value}>{value}</span>
        <span className={styles.subTitle}>{subTitle}</span>
      </div>
    </div>
  );
};

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.leftArrow)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.rightArrow)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

interface PreferredLanguageProps {
  value: string;
  onChange: (property: string, value: any) => void;
}

const PreferredLanguage = ({value, onChange}: PreferredLanguageProps) => {

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedLanguage =
      languageOptions.find(option => option.value === value) ||
      languageOptions[0];

    setSelected(selectedLanguage);
  }, [value]);

  const [selected, setSelected] = useState(languageOptions.find(option => option.value === value) || languageOptions[0]);
  const [open, setOpen] = useState(false);

  const animation = useSpring({
    height: open ? menuRef.current?.scrollHeight ?? 0 : 0,
    opacity: open ? 1 : 0,
    config: {
      tension: 120,
      friction: 30,
    },
  });

  const handleLanguageSelect = (language) => {
    onChange("language", language.value);
    setSelected(language);
    setOpen(false);
  }

  return (
    <div className={styles.languageDropdown}>
      <Dropdown show={open} onToggle={() => setOpen(prev => !prev)}>
        <Dropdown.Toggle
          id="preferred-language"
          as="button"
          className={styles.toggle}
        >
          {selected.label}
          <i className={cx("paperflite-dropdown", styles.dropdown)} />
        </Dropdown.Toggle>
      </Dropdown>

      <animated.div
        className={cx(styles.menuWrapper, {
          [styles.open]: open,
        })}
        style={animation}
      >
        <div className={styles.menu} ref={menuRef}>
          {languageOptions.map((language) => {
            const isSelected = selected.value === language.value;

            return (
              <div
                key={language.value}
                className={cx(styles.menuItem, {
                  [styles.selected]: isSelected,
                })}
                onClick={() => handleLanguageSelect(language)}
              >
                <i
                  className={cx(
                    isSelected
                      ? "paperflite-radio-button-selected"
                      : "paperflite-radio-button-unselected"
                  )}
                  aria-hidden="true"
                />
                <span>{language.label}</span>
              </div>
            );
          })}
        </div>
      </animated.div>
    </div>
  );
};

